<template>
  <div>
    <div class="px-4">
      <div class="body p-4">
        <span class="text-primary h2 d-flex align-items-center">
          <span class="text-gray"> Configurar NFSe</span>
        </span>

        <div class="card p-4 mt-4">
          <div class="p-2">
            <div
              v-if="isExistingConfig && !isActive"
              class="alert alert-warning mb-4"
              role="alert"
            >
              Configuração aguardando ativação por um administrador. Emissões estarão bloqueadas até a ativação.
            </div>

            <form @submit.prevent="submitForm">
              <div class="row text-start">
                <div class="col-lg-4 col-md-6 col-sm-12 text-primary mb-4">
                  <label for="input-cnpj" class="form-label mb-2">
                    CNPJ*
                  </label>
                  <input
                    type="text"
                    id="input-cnpj"
                    class="form-control"
                    placeholder="00.000.000/0000-00"
                    v-model="displayCNPJ"
                    disabled
                  />
                </div>
                <div class="col-lg-4 col-md-6 col-sm-12 text-primary mb-4">
                  <label class="form-label mb-2">Localidade de Emissão*</label>
                  <v-select
                    v-model="selectedMunicipio"
                    :options="filteredMunicipios"
                    :get-option-label="(m) => `${m.nome} (${m.uf})`"
                    :filterable="false"
                    @search="onMunicipioSearch"
                    placeholder="Digite o nome da cidade"
                    no-drop-on-empty
                  >
                    <template #no-options="{ search }">
                      <span v-if="!search || search.length < 2">
                        Digite o nome da cidade para pesquisar
                      </span>
                      <span v-else>Nenhuma cidade encontrada</span>
                    </template>
                  </v-select>
                </div>
                <div class="col-lg-4 col-md-6 col-sm-12 text-primary mb-4">
                  <label for="input-phone" class="form-label mb-2">
                    Telefone*
                  </label>
                  <input
                    type="tel"
                    id="input-phone"
                    class="form-control"
                    placeholder="(00) 00000-0000"
                    v-model="displayFone"
                    v-mask="['(##) ####-####', '(##) #####-####']"
                    required
                  />
                </div>
                <div class="col-lg-4 col-md-6 col-sm-12 text-primary mb-4">
                  <label for="input-email" class="form-label mb-2">
                    E-mail*
                  </label>
                  <input
                    type="email"
                    id="input-email"
                    class="form-control"
                    placeholder="Informe o e-mail"
                    v-model="model.email"
                    required
                  />
                </div>
                <div class="col-lg-4 col-md-6 col-sm-12 text-primary mb-4">
                  <label for="input-op-simp-nac" class="form-label mb-2">
                    Op&ccedil;&atilde;o pelo Simples Nacional*
                  </label>
                  <select
                    id="input-op-simp-nac"
                    class="form-select"
                    v-model="model.opSimpNac"
                    @change="resetRegEspTribIfLocked"
                    required
                  >
                    <option disabled value="">Selecione</option>
                    <option value="2">2 - MEI</option>
                    <option value="3">3 - ME/EPP do Simples Nacional</option>
                  </select>
                </div>
                <div
                  v-if="model.opSimpNac === '3'"
                  class="col-lg-4 col-md-6 col-sm-12 text-primary mb-4"
                >
                  <label for="input-reg-ap-trib-sn" class="form-label mb-2">
                    Regime de apura&ccedil;&atilde;o tribut&aacute;ria (SN)*
                  </label>
                  <select
                    id="input-reg-ap-trib-sn"
                    class="form-select"
                    v-model="model.regApTribSN"
                    @change="resetRegEspTribIfLocked"
                  >
                    <option disabled value="">Selecione</option>
                    <option value="1">1 - Tributos federais e municipal pelo SN</option>
                    <option value="2">2 - Tributos federais pelo SN; ISSQN pela NFS-e municipal</option>
                    <option value="3">3 - Tributos federais e municipal pela NFS-e</option>
                  </select>
                </div>
                <div class="col-lg-4 col-md-6 col-sm-12 text-primary mb-4">
                  <label for="input-reg-esp-trib" class="form-label mb-2">
                    Regime especial de tributa&ccedil;&atilde;o*
                  </label>
                  <select
                    id="input-reg-esp-trib"
                    class="form-select"
                    :class="{ 'is-invalid': !!regEspTribError }"
                    v-model="model.regEspTrib"
                    :disabled="regEspTribDisabled"
                    required
                  >
                    <option disabled value="">Selecione</option>
                    <option
                      v-for="opt in regEspTribOptions"
                      :key="opt.value"
                      :value="opt.value"
                    >
                      {{ opt.label }}
                    </option>
                  </select>
                  <div v-if="regEspTribError" class="invalid-feedback d-block">
                    {{ regEspTribError }}
                  </div>
                  <div
                    v-if="regEspTribLocked"
                    class="form-text"
                    :class="{ 'text-warning': !regEspTribDisabled }"
                  >
                    {{ regEspTribLockedMessage }}
                    <template v-if="!regEspTribDisabled">
                      Este cadastro está com um valor incompatível — selecione
                      "0 - Nenhum" para poder salvar.
                    </template>
                  </div>
                </div>
                <div class="col-lg-4 col-md-6 col-sm-12 text-primary mb-4">
                  <label for="input-serie" class="form-label mb-2">
                    S&eacute;rie
                  </label>
                  <input
                    type="text"
                    id="input-serie"
                    class="form-control"
                    placeholder="1"
                    v-model="model.serie"
                  />
                </div>
                <div
                  v-if="isExistingConfig"
                  class="col-lg-4 col-md-6 col-sm-12 text-primary mb-4"
                >
                  <label for="input-ndps" class="form-label mb-2">
                    &Uacute;ltimo nDPS
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="1"
                    id="input-ndps"
                    class="form-control"
                    v-model="model.ultimoNdps"
                  />
                  <div class="form-text">
                    Uso apenas para migração de outro emissor. Só é possível avançar o número —
                    reduzir causa rejeição por duplicidade na prefeitura.
                  </div>
                </div>
              </div>
              <span class="text-center mt-4">
                <button
                  type="submit"
                  class="btn px-4 font-weight-bold btn-primary text-white btn-shadow"
                  :disabled="loaderActive || prefillCnpjEmpty"
                >
                  <span class="mx-4" v-if="!loaderActive">Salvar</span>
                  <span class="mx-4" v-else>Aguarde...</span>
                </button>
              </span>
            </form>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import "vue-select/dist/vue-select.css";
import municipios from "@/data/nfse/municipios.json";
import api from "@/services/api";
import {
  searchMunicipios,
  isRegEspTribLocked,
  REG_ESP_TRIB_OPTIONS,
  REG_ESP_TRIB_VALUES,
  REG_ESP_TRIB_LOCKED_MESSAGE,
} from "@/utils/nfseHelpers";
import { useToast } from "vue-toastification";

const toast = useToast();

export default {
  name: "nfse-config",
  components: {},
  data() {
    return {
      displayCNPJ: "",
      displayFone: "",
      loaderActive: false,
      isExistingConfig: false,
      isActive: false,
      prefillCnpjEmpty: false,
      loadedUltimoNdps: 0,
      selectedMunicipio: null,
      municipioSearch: "",
      regEspTribError: "",
      regEspTribOptions: REG_ESP_TRIB_OPTIONS,
      regEspTribLockedMessage: REG_ESP_TRIB_LOCKED_MESSAGE,
      model: {
        fone: "",
        email: "",
        opSimpNac: "3",
        regApTribSN: "",
        regEspTrib: "",
        serie: "1",
        ultimoNdps: 0,
      },
    };
  },
  computed: {
    filteredMunicipios() {
      return searchMunicipios(municipios, this.municipioSearch);
    },
    regEspTribLocked() {
      return isRegEspTribLocked(this.model.opSimpNac, this.model.regApTribSN);
    },
    // Cadastro legado pode chegar travado com um valor != "0" — nesse caso o
    // seletor continua habilitado para o usuário corrigir para "0".
    regEspTribDisabled() {
      return this.regEspTribLocked && this.model.regEspTrib === "0";
    },
  },
  watch: {
    displayFone(val) {
      this.model.fone = this.onlyDigits(val);
    },
    "model.regEspTrib"() {
      this.regEspTribError = "";
    },
  },
  async mounted() {
    await this.loadNfseConfig();
  },
  methods: {
    async loadNfseConfig() {
      try {
        const { data } = await api.get("/company/nfse/config", {
          skipSessionExpiredHandler: true,
        });
        if (!data || data.configured === false) {
          await this.loadNfsePrefill();
          return;
        }
        this.isExistingConfig = true;
        this.fillModel(data);
      } catch (e) {
        let message =
          "Ocorreu um erro ao carregar a configuração da NFSe. Recarregue a página e tente novamente.";
        if (e.response && e.response.data && e.response.data.message) {
          message = e.response.data.message;
        }
        toast.error(message);
      }
    },
    // Chamado só a partir do @change dos selects de op_simp_nac/reg_ap_trib_sn
    // — fillModel() atribui os campos diretamente, sem disparar "change", então
    // um cadastro legado com combinação proibida chega intacto para o usuário
    // corrigir, sem precisar de guarda contra o carregamento inicial.
    resetRegEspTribIfLocked() {
      if (this.regEspTribLocked) this.model.regEspTrib = "0";
    },
    fillModel(config) {
      this.displayCNPJ       = this.formatCNPJ(this.onlyDigits(config.cnpj || ""));
      this.model.fone        = this.onlyDigits(String(config.fone || ""));
      this.displayFone       = this.formatPhone(this.model.fone);
      this.model.email       = String(config.email || "");
      this.model.opSimpNac   = config.op_simp_nac   != null ? String(config.op_simp_nac)   : "";
      this.model.regApTribSN = config.reg_ap_trib_sn != null ? String(config.reg_ap_trib_sn) : "";
      this.model.regEspTrib  = config.reg_esp_trib  != null ? String(config.reg_esp_trib)  : "";
      this.model.serie       = config.serie ?? "1";
      this.model.ultimoNdps  = config.ultimo_n_dps ?? 0;
      this.loadedUltimoNdps  = config.ultimo_n_dps ?? 0;
      this.isActive          = config.is_active ?? false;

      if (config.c_loc_emi) {
        this.selectedMunicipio =
          municipios.find(
            (m) => m.codigo === String(config.c_loc_emi)
          ) || null;
      }
    },
    async loadNfsePrefill() {
      try {
        const { data } = await api.get("/company/nfse/prefill", {
          skipSessionExpiredHandler: true,
        });
        this.fillPrefillModel(data || {});
      } catch (e) {
        let message =
          "Ocorreu um erro ao carregar os dados da empresa para a NFSe.";
        if (e.response && e.response.data && e.response.data.message) {
          message = e.response.data.message;
        }
        toast.error(message);
      }
    },
    fillPrefillModel(prefill) {
      this.prefillCnpjEmpty = !prefill.cnpj;

      if (prefill.cnpj) {
        this.displayCNPJ = this.formatCNPJ(this.onlyDigits(prefill.cnpj));
      }
      if (prefill.phone) {
        this.model.fone = this.onlyDigits(String(prefill.phone));
        this.displayFone = this.formatPhone(this.model.fone);
      }
      if (prefill.email) {
        this.model.email = String(prefill.email);
      }
    },
    onlyDigits(value) {
      return String(value || "").replace(/\D/g, "");
    },
    formatCNPJ(digits) {
      const d = String(digits || "").replace(/\D/g, "").slice(0, 14);
      if (d.length <= 2) return d;
      if (d.length <= 5) return `${d.slice(0, 2)}.${d.slice(2)}`;
      if (d.length <= 8) return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5)}`;
      if (d.length <= 12) return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8)}`;
      return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8, 12)}-${d.slice(12, 14)}`;
    },
    formatPhone(digits) {
      const d = String(digits || "").replace(/\D/g, "").slice(0, 11);
      if (!d) return "";
      if (d.length <= 2) return `(${d}`;
      if (d.length <= 6) return `(${d.slice(0, 2)}) ${d.slice(2)}`;
      if (d.length === 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
      return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
    },
    onMunicipioSearch(search) {
      this.municipioSearch = search;
    },
    buildPayload() {
      return {
        c_loc_emi: this.selectedMunicipio?.codigo ?? "",
        fone: this.model.fone.trim(),
        email: this.model.email.trim(),
        op_simp_nac: this.model.opSimpNac,
        reg_ap_trib_sn: this.model.regApTribSN,
        reg_esp_trib: this.model.regEspTrib,
        serie: this.model.serie || "1",
        ultimo_n_dps: Number(this.model.ultimoNdps) || 0,
      };
    },
    validateForm(payload) {
      if (!/^\d{7}$/.test(payload.c_loc_emi)) {
        toast.error("Informe o código da localidade de emissão.");
        return false;
      }

      if (!payload.fone) {
        toast.error("Informe o fone.");
        return false;
      }

      if (!payload.email) {
        toast.error("Informe o e-mail.");
        return false;
      }

      if (!["2", "3"].includes(payload.op_simp_nac)) {
        toast.error("Informe a opção pelo Simples Nacional.");
        return false;
      }

      if (
        payload.op_simp_nac === "3" &&
        !["1", "2", "3"].includes(payload.reg_ap_trib_sn)
      ) {
        toast.error("Informe o regime de apuração tributária (SN).");
        return false;
      }

      if (!REG_ESP_TRIB_VALUES.includes(payload.reg_esp_trib)) {
        toast.error("Informe o regime especial de tributação.");
        return false;
      }

      if (
        isRegEspTribLocked(payload.op_simp_nac, payload.reg_ap_trib_sn) &&
        payload.reg_esp_trib !== "0"
      ) {
        this.regEspTribError = REG_ESP_TRIB_LOCKED_MESSAGE;
        toast.error(REG_ESP_TRIB_LOCKED_MESSAGE);
        return false;
      }

      if (!Number.isInteger(payload.ultimo_n_dps) || payload.ultimo_n_dps < 0) {
        toast.error("O último nDPS deve ser um número inteiro maior ou igual a zero.");
        return false;
      }

      if (this.isExistingConfig && payload.ultimo_n_dps < this.loadedUltimoNdps) {
        toast.error(
          `O último nDPS não pode ser menor que ${this.loadedUltimoNdps} — reduzir a faixa causaria rejeição por duplicidade.`
        );
        return false;
      }

      return true;
    },
    async submitForm() {
      this.regEspTribError = "";
      const payload = this.buildPayload();
      if (!this.validateForm(payload)) return;

      try {
        this.loaderActive = true;
        const { data } = await api.put("/company/nfse/config", payload);
        this.isExistingConfig = true;
        if (data) this.fillModel(data);
        toast.success("Configuração da NFSe salva com sucesso.");
      } catch (e) {
        let message =
          "Ocorreu um erro ao salvar a configuração da NFSe. Verifique os dados e tente novamente.";
        const responseData = e.response && e.response.data;
        if (responseData) {
          if (responseData.code === "E_NFSE_NDPS_RETROCESSION") {
            message = `O último nDPS não pode ser reduzido — reduzir a faixa causaria rejeição por duplicidade na prefeitura.`;
          } else if (responseData.code === "E_NFSE_REGESPTRIB_INVALID") {
            message = responseData.message || REG_ESP_TRIB_LOCKED_MESSAGE;
            this.regEspTribError = message;
          } else if (responseData.code === "E_NFSE_NOT_SIMPLES") {
            message =
              responseData.message ||
              "A empresa não está cadastrada como optante do Simples Nacional. Verifique o cadastro antes de configurar a NFSe.";
          } else if (responseData.message) {
            message = responseData.message;
          }
        }
        toast.error(message);
      } finally {
        this.loaderActive = false;
      }
    },
  },
};
</script>
